import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { Bell } from "lucide-react";
import { formatDistanceToNow, isPast, subHours } from "date-fns";
import { useNavigate } from "react-router-dom";

interface Notification {
  id: string;
  type: 'overdue_task' | 'new_lead';
  title: string;
  description: string;
  time: string;
  link: string;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    loadNotifications();
    // Poll every 60 seconds
    const interval = setInterval(loadNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadNotifications = async () => {
    const items: Notification[] = [];

    // Overdue tasks
    const { data: tasks } = await supabase
      .from("tasks")
      .select("id, title, due_date, customer:customers(first_name, last_name)")
      .is("completed_at", null)
      .lt("due_date", new Date().toISOString())
      .order("due_date", { ascending: true })
      .limit(5);

    (tasks || []).forEach((t: any) => {
      items.push({
        id: `task-${t.id}`,
        type: 'overdue_task',
        title: t.title,
        description: t.customer ? `${t.customer.first_name} ${t.customer.last_name}` : '',
        time: t.due_date,
        link: '/admin/tasks',
      });
    });

    // New leads in last hour
    const oneHourAgo = subHours(new Date(), 1).toISOString();
    const { data: leads } = await supabase
      .from("contact_submissions")
      .select("id, name, created_at")
      .eq("status", "new")
      .gte("created_at", oneHourAgo)
      .order("created_at", { ascending: false })
      .limit(5);

    (leads || []).forEach((l: any) => {
      items.push({
        id: `lead-${l.id}`,
        type: 'new_lead',
        title: `New lead: ${l.name}`,
        description: 'Form submission',
        time: l.created_at,
        link: '/admin/leads',
      });
    });

    setNotifications(items);
  };

  const handleClick = (n: Notification) => {
    setIsOpen(false);
    navigate(n.link);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {notifications.length > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {notifications.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="px-4 py-3 border-b">
          <h3 className="font-semibold text-sm">Notifications</h3>
        </div>
        <div className="max-h-[300px] overflow-y-auto">
          {notifications.length > 0 ? (
            notifications.map(n => (
              <div
                key={n.id}
                className="px-4 py-3 hover:bg-muted/50 cursor-pointer border-b last:border-0"
                onClick={() => handleClick(n)}
              >
                <div className="flex items-start gap-2">
                  <span className="text-sm flex-shrink-0">
                    {n.type === 'overdue_task' ? '⚠️' : '🔔'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{n.title}</div>
                    {n.description && <div className="text-xs text-muted-foreground">{n.description}</div>}
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {formatDistanceToNow(new Date(n.time), { addSuffix: true })}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">
              All clear! No new notifications.
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
